import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { PosAnchorKind, PosUser, PosUserCreateRequest, ScopeKind } from "@tilt/admin-sdk";

import { ModalOverlay } from "./ModalOverlay";
import { RoleSelect } from "./RoleSelect";
import { WorkplacePicker, type WorkplacePickerValue } from "./WorkplacePicker";
import { EMPTY_WORKPLACE } from "./workplace-picker-constants";

/**
 * Inline panel rendered inside the expanded Users row (next to the
 * Memberships section). Lists every pos_user_tbl row whose email
 * matches this admin user's email - that surfaces all POS accounts
 * the human holds across anchors. Shows a "Grant POS access" button
 * that opens a tiny modal: pick anchor (partner OR organization),
 * pick initial role slug, submit.
 *
 * The POS user record is independent of the admin user_tbl row;
 * they're correlated by email only (the V0042 contract). Disabling
 * here flips ``pos_user_tbl.is_disabled`` + AdminDisableUser on the
 * Cognito side; re-enabling reverses both.
 */
export function PosAccessPanel({ userEmail }: { readonly userEmail: string }): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["pos-users", "by-email", userEmail],
    queryFn: () => apiClient.listPosUsers({ email: userEmail, include_disabled: true, limit: 50 }),
  });

  const [creating, setCreating] = useState(false);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["pos-users", "by-email", userEmail] });
  };

  const disable = useMutation({
    mutationFn: (id: string) => apiClient.disablePosUser(id),
    onSuccess: invalidate,
  });
  const enable = useMutation({
    mutationFn: (id: string) => apiClient.enablePosUser(id),
    onSuccess: invalidate,
  });

  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">POS access</h4>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          Grant POS access
        </button>
      </div>

      {list.isPending ? (
        <p className="text-xs text-slate-500">Loading POS accounts…</p>
      ) : list.isError ? (
        <p className="text-xs text-red-600">Failed to load POS accounts: {describe(list.error)}</p>
      ) : list.data?.pos_users.length === 0 ? (
        <p className="text-xs text-slate-500">
          No POS accounts for {userEmail}. Use Grant POS access to provision one anchored at a
          specific partner or organization.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.data?.pos_users.map((p) => (
            <PosAccountRow
              key={p.id}
              pu={p}
              onDisable={() => disable.mutate(p.id)}
              onEnable={() => enable.mutate(p.id)}
              busy={disable.isPending || enable.isPending}
            />
          ))}
        </ul>
      )}

      {creating && (
        <GrantPosAccessDialog
          email={userEmail}
          onClose={() => setCreating(false)}
          onCreated={() => {
            invalidate();
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function PosAccountRow({
  pu,
  onDisable,
  onEnable,
  busy,
}: {
  readonly pu: PosUser;
  readonly onDisable: () => void;
  readonly onEnable: () => void;
  readonly busy: boolean;
}) {
  const anchorLabel =
    pu.anchor.kind === "partner"
      ? (pu.anchor.partner_name ?? pu.anchor.id)
      : (pu.anchor.organization_name ?? pu.anchor.id);
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <div>
        <div className="font-medium text-slate-800">{anchorLabel}</div>
        <div className="text-xs text-slate-500">
          {pu.anchor.kind === "partner" ? "Partner" : "Organization"} • created{" "}
          {pu.created_at?.slice(0, 10)}
          {pu.is_disabled && (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              disabled
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={pu.is_disabled ? onEnable : onDisable}
        className={cn(
          "rounded border px-2 py-1 text-xs font-medium",
          pu.is_disabled
            ? "border-slate-300 text-slate-700 hover:bg-slate-50"
            : "border-red-300 text-red-700 hover:bg-red-50",
        )}
      >
        {pu.is_disabled ? "Re-enable" : "Disable"}
      </button>
    </li>
  );
}

/**
 * Anchor-aware "give this person POS access" dialog. Replaces the old
 * UUID-input + unfiltered-role-list version that the user flagged on
 * 2026-04-25 - now uses WorkplacePicker (name-based hierarchical
 * picker) for the anchor + initial-membership scope, and RoleSelect
 * for the role list (which narrows to roles whose valid_scopes
 * include the picked scope kind).
 *
 * Two-step flow:
 *
 *   1) Pick anchor - Partner OR Organization. The WorkplacePicker
 *      offers Partner-only or Org-only options depending on the
 *      anchor radio button at the top.
 *   2) Pick the initial membership - re-uses WorkplacePicker, but
 *      bound to the anchor's subtree (partner anchor → all three
 *      depths; org anchor → org + location only). The role list
 *      then re-filters to (pool=pos, scope_kind=picked_kind).
 */
function GrantPosAccessDialog({
  email,
  onClose,
  onCreated,
}: {
  readonly email: string;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const [anchorKind, setAnchorKind] = useState<PosAnchorKind>("partner");
  const [anchor, setAnchor] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [grantInitial, setGrantInitial] = useState(true);
  const [scope, setScope] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);
  const [roleSlug, setRoleSlug] = useState<string>("");

  // The membership scope picker is constrained by the anchor: a
  // partner anchor allows partner/org/loc; an org anchor allows only
  // org/loc and pins the org to the chosen anchor id.
  const allowedScopeKinds = useMemo<readonly ScopeKind[]>(
    () =>
      anchorKind === "partner"
        ? (["partner", "organization", "location"] as const)
        : (["organization", "location"] as const),
    [anchorKind],
  );

  // Reset the scope picker when the anchor flips so a stale selection
  // from the previous anchor's subtree doesn't leak through.
  useEffect(() => {
    setScope(EMPTY_WORKPLACE);
    setRoleSlug("");
  }, [anchorKind, anchor.id]);

  const anchorReady = anchor.kind === anchorKind && anchor.id !== "";
  const scopeReady = !grantInitial || (scope.kind !== null && scope.id !== "" && roleSlug !== "");
  const submitDisabled = !anchorReady || !scopeReady;

  const mutate = useMutation({
    mutationFn: () => {
      if (!anchorReady) {
        return Promise.reject(new Error("Pick a workplace for the POS anchor."));
      }
      const body: PosUserCreateRequest = {
        email,
        anchor_kind: anchorKind,
        anchor_id: anchor.id,
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
      };
      if (grantInitial) {
        if (scope.kind === null || !scope.id || !roleSlug) {
          return Promise.reject(
            new Error("Pick a workplace and a role for the initial membership."),
          );
        }
        body.initial_membership = {
          scope_kind: scope.kind,
          scope_id: scope.id,
          role_slug: roleSlug,
        };
      }
      return apiClient.createPosUser(body);
    },
    onSuccess: onCreated,
  });

  return (
    <ModalOverlay ariaLabel="Grant POS access" onClose={onClose}>
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-white p-6 shadow-xl">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Grant POS access</h3>
          <p className="mt-1 text-xs text-slate-500">
            Provisions a POS account for <span className="font-medium">{email}</span> at the
            workplace you pick. Cognito sends the invite email; the user signs in at that
            workplace's POS URL.
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase text-slate-500">
            Where will they work?
          </legend>
          <div className="flex gap-2">
            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                checked={anchorKind === "partner"}
                onChange={() => {
                  setAnchorKind("partner");
                  setAnchor(EMPTY_WORKPLACE);
                }}
              />
              At a partner
            </label>
            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                checked={anchorKind === "organization"}
                onChange={() => {
                  setAnchorKind("organization");
                  setAnchor(EMPTY_WORKPLACE);
                }}
              />
              At an organization
            </label>
          </div>
          <WorkplacePicker
            value={anchor}
            onChange={setAnchor}
            allowedKinds={anchorKind === "partner" ? ["partner"] : ["organization"]}
            idPrefix="pos-anchor"
          />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase text-slate-500">Profile</legend>
          <div className="flex gap-2">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              className="block w-1/2 rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              className="block w-1/2 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={grantInitial}
              onChange={(e) => setGrantInitial(e.target.checked)}
            />
            Grant an initial role
          </label>
          {grantInitial && (
            <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-600">
                Pick the workplace where this role applies (must be inside the anchor) and the role
                itself. Role list filters to what's valid for the depth you picked.
              </p>
              <WorkplacePicker
                value={scope}
                onChange={setScope}
                allowedKinds={allowedScopeKinds}
                {...(anchorKind === "organization"
                  ? {
                      boundOrganizationId: anchor.id,
                      ...(anchor.labels.organization
                        ? { boundOrganizationName: anchor.labels.organization }
                        : {}),
                    }
                  : {})}
                idPrefix="pos-scope"
              />
              <RoleSelect
                pool="pos"
                scopeKind={scope.kind}
                value={roleSlug}
                onChange={setRoleSlug}
                id="pos-role"
              />
            </div>
          )}
        </fieldset>

        {mutate.isError && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {describe(mutate.error)}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={mutate.isPending || submitDisabled}
            onClick={() => mutate.mutate()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {mutate.isPending ? "Granting…" : "Grant access"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
